const ro = 'ro-RO';

export function formatEur(value: number): string {
  return new Intl.NumberFormat(ro, { style: 'currency', currency: 'EUR' }).format(value);
}

export function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat(ro, { style: 'currency', currency }).format(value);
}

/** Per-model AI rows are often under a cent: four decimals, not two. */
export function formatEurPrecise(value: number): string {
  return new Intl.NumberFormat(ro, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

export function formatInt(value: number): string {
  return new Intl.NumberFormat(ro).format(value);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(ro, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(ro, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "acum 3 min", "acum 2 h", "acum 4 zile". */
export function formatAgo(iso: string | null): string {
  if (!iso) return 'niciodată';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'acum';
  if (minutes < 60) return `acum ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `acum ${hours} h`;
  return `acum ${Math.round(hours / 24)} zile`;
}

export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  if (days > 0) return `${days} z ${hours} h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours} h ${minutes} min`;
}

const PROVIDER_LABELS: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI' };

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

const ACTION_LABELS: Record<string, string> = {
  'operator.login': 'Autentificare în consolă',
  'tenant.create': 'Client creat',
  'tenant.update': 'Client modificat',
  'tenant.suspend': 'Acces oprit (OFF)',
  'tenant.activate': 'Acces pornit (ON)',
  'tenant.rotate_key': 'Cheie de instanță regenerată',
  'tenant.modules': 'Module modificate',
  'tenant.optInModules': 'Module la cerere modificate',
  'tenant.ai_models': 'Modele AI modificate',
  'tenant.ai_billing': 'Facturare AI modificată',
  'tenant.delete': 'Client șters',
  'invoice.create': 'Factură adăugată',
  'invoice.update': 'Factură modificată',
  'invoice.delete': 'Factură ștearsă',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export const INVOICE_STATUS_LABELS = {
  DRAFT: 'Ciornă',
  SENT: 'Emisă',
  PAID: 'Plătită',
  CANCELLED: 'Anulată',
} as const;

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysIso(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

export const TIER_LABELS: Record<'haiku' | 'sonnet' | 'opus', string> = {
  haiku: 'Haiku',
  sonnet: 'Sonnet',
  opus: 'Opus',
};

/** "claude-haiku-5-5" → "Haiku 5.5", "claude-haiku-4-5-20251001" → "Haiku 4.5"; other ids as they are. */
export function modelLabel(id: string): string {
  const m = /^claude-(haiku|sonnet|opus)-(\d+)-(\d+)/.exec(id);
  if (!m) return id;
  return `${TIER_LABELS[m[1] as keyof typeof TIER_LABELS]} ${m[2]}.${m[3]}`;
}

/** "2026-10" → "octombrie 2026". */
export function formatMonth(month: string): string {
  const [y, mo] = month.split('-').map(Number);
  if (!y || !mo) return month;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(ro, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
